// src/components/Header.js

import React, { useMemo, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  Image,
  useWindowDimensions,
  StyleSheet,
  Text,
  Modal,
  Pressable,
  findNodeHandle,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { useTranslation } from "../i18n";

const Header = ({ backgroundColor = "#FFFFFF" }) => {
  const { width } = useWindowDimensions();
  const { currentLang, setLanguage } = useTranslation();
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const isMobile = width < 520;
  const logoSize = isMobile ? 90 : 120;

  // Ref to anchor the dropdown under the button
  const selectorRef = useRef(null);

  const languages = useMemo(
    () => [
      { code: "fr", name: "Français", flag: "🇫🇷" },
      { code: "en", name: "English", flag: "🇬🇧" },
    ],
    []
  );

  const currentLanguage = useMemo(
    () => languages.find((l) => l.code === currentLang) || languages[0],
    [languages, currentLang]
  );

  const handleLanguageChange = async (langCode) => {
    // Always close menu immediately for snappy UI
    setDropdownVisible(false);

    if (langCode !== currentLang) {
      await setLanguage(langCode);
    }
  };

  return (
    <View style={[styles.header, { backgroundColor }]}>
      <View style={styles.headerContent}>
        {/* ✅ Left spacer (keeps CUY centered) */}
        <View style={{ width: logoSize, height: logoSize }} />

        {/* ✅ Center CUY logo (absolute so it never pushes language selector off-screen) */}
        <View style={styles.centerLogoWrap} pointerEvents="none">
          <Image
            source={require("../../assets/CUY.jpg")}
            style={[
              styles.cuyLogo,
              {
                // ✅ responsive: big but capped so nothing gets cut off
                width: Math.min(logoSize * 2.0, width * 0.55),
                height: logoSize * 1.0,
              },
            ]}
            resizeMode="contain"
          />
        </View>

        {/* ✅ Language Selector on the right */}
        <View style={styles.languageSelectorContainer} ref={selectorRef}>
          <TouchableOpacity
            style={styles.languageSelectorButton}
            onPress={() => setDropdownVisible(true)}
            activeOpacity={0.7}
          >
            <View style={styles.languageCurrent}>
              <Text style={styles.languageFlag}>{currentLanguage.flag}</Text>
              <Text style={styles.languageCode}>
                {currentLanguage.code.toUpperCase()}
              </Text>
              <MaterialIcons name="arrow-drop-down" size={20} color="#6B7280" />
            </View>
          </TouchableOpacity>
        </View>
      </View>

      {/* Dropdown rendered via Modal to avoid touch/zIndex issues */}
      <Modal
        visible={dropdownVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setDropdownVisible(false)}
      >
        {/* Tap outside to close */}
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setDropdownVisible(false)}
        />

        {/* ✅ Menu wrapper aligned to the right */}
        <View pointerEvents="box-none" style={styles.modalMenuWrapper}>
          <View style={styles.dropdownMenu}>
            {languages.map((language) => {
              const active = currentLang === language.code;
              return (
                <TouchableOpacity
                  key={language.code}
                  style={[styles.dropdownItem, active && styles.dropdownItemActive]}
                  onPress={() => handleLanguageChange(language.code)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.dropdownFlag}>{language.flag}</Text>

                  <View style={styles.dropdownTextContainer}>
                    <Text
                      style={[
                        styles.dropdownLanguageName,
                        active && styles.dropdownLanguageNameActive,
                      ]}
                    >
                      {language.name}
                    </Text>
                    <Text
                      style={[
                        styles.dropdownLanguageCode,
                        active && styles.dropdownLanguageCodeActive,
                      ]}
                    >
                      {language.code.toUpperCase()}
                    </Text>
                  </View>

                  {active && (
                    <MaterialIcons name="check" size={18} color="#008751" />
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderBottomWidth: 2,
    borderBottomColor: "#E5E7EB",
    zIndex: 100,
  },

  // ✅ Allow wrapping on small screens so nothing clips
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },

  // ✅ Center logo positioning (does not affect layout flow)
  centerLogoWrap: {
    position: "absolute",
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
  },

  logoContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  logoImage: {},
  cuyLogo: {},

  languageSelectorContainer: {
    // keep it simple; Modal handles layering
  },
  languageSelectorButton: {
    backgroundColor: "#F3F4F6",
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 100,
    maxWidth: "100%", // ✅ prevents overflow/clipping
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  languageCurrent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  languageFlag: {
    fontSize: 16,
  },
  languageCode: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },

  // Modal overlay
  modalOverlay: {
    flex: 1,
    backgroundColor: "transparent",
  },

  // ✅ Menu wrapper: right aligned under header
  modalMenuWrapper: {
    position: "absolute",
    top: 78,
    right: 16,
    width: 220,
  },

  // Dropdown Menu
  dropdownMenu: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingVertical: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 7,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  dropdownItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  dropdownItemActive: {
    backgroundColor: "#F0F9FF",
  },
  dropdownFlag: {
    fontSize: 20,
    width: 30,
  },
  dropdownTextContainer: {
    flex: 1,
  },
  dropdownLanguageName: {
    fontSize: 14,
    fontWeight: "500",
    color: "#374151",
    marginBottom: 2,
  },
  dropdownLanguageNameActive: {
    color: "#008751",
    fontWeight: "600",
  },
  dropdownLanguageCode: {
    fontSize: 12,
    color: "#6B7280",
  },
  dropdownLanguageCodeActive: {
    color: "#008751",
    fontWeight: "500",
  },
});

export default Header;
